import http from '@/services/http'
import { warehouseUrl } from '@/services/url'
import type * as Types from './type'
import { retryWhen } from '@/utils/retry/retryWhen'
import { yuanify } from '@yy/one-currency'
import { createFnWithUrl } from '@/utils/createFnWithUrl'
import { formatTimezone } from '@/utils'

interface AreaCodesWrapperDTO {
  /**区域别名 */
  alias?: string
  /**地址节点编码列表 */
  addressCodes?: string[]
  /**国家编码 */
  countryCode?: string
}

interface ExpressTypeInsuranceSettingRespDTO {
  /**保费设置模版唯一编码 */
  settingSeq?: string
  /**保费模版设置价值区间最小值
（标准货币单位 * 100） */
  minInsuredAmount?: number
  /**保费模版设置价值区间最大值
（标准货币单位 * 100）
Long.MAX_VALUE表示无限 */
  maxInsuredAmount?: number
  /**币种 */
  currencyCode?: string
}

interface SelectablePickTimeDTO {
  /**日期 */
  date?: string
  /**时间段 */
  timeIntervals?: string[]
}

export interface MatchConfigType {
  /** 平台物流匹配方式 1-全匹配 2-包含 */
  matchType: number
  /** 平台物流编码 */
  platformCodes?: string[]
  /** 平台物流编码 */
  platformCode?: string
  /** 平台 */
  platformSource?: string
}

export interface LogisticSellerMatchConfig {
  platformMatch: MatchConfigType
}
export interface LogisticMatchConfig {
  platformMatch: MatchConfigType[]
}

interface ChannelExpressTypeConfigDTO {
  /**渠道产品 id */
  channelExpressTypeSeq?: string
  /**默认地址开关；0：关闭；1：开启 */
  senderDefaultAddressSwitch?: number
  /**默认发件国家（约束：senderCountry must be less than or equals to 100） */
  senderCountry?: string
  /**默认发件省份（约束：senderProvince must be less than or equals to 128） */
  senderProvince?: string
  /**默认发件城市（约束：senderCity must be less than or equals to 128） */
  senderCity?: string
  /**默认发件地区（约束：senderDistrict must be less than or equals to 128） */
  senderDistrict?: string
  /**默认发件街道（约束：senderTown must be less than or equals to 128） */
  senderTown?: string
  /**默认发件地址1（约束：senderAddress must be less than or equals to 256） */
  senderAddress?: string
  /**默认发件地址2（约束：senderAddressTwo must be less than or equals to 256） */
  senderAddressTwo?: string
  /**默认发件人（约束：senderName must be less than or equals to 32） */
  senderName?: string
  /**默认发件电话（约束：senderPhone must be less than or equals to 32） */
  senderPhone?: string
  /**默认发件手机（约束：senderMobile must be less than or equals to 32） */
  senderMobile?: string
  /**默认发件邮箱（约束：senderEmail must be less than or equals to 64） */
  senderEmail?: string
  /**默认发件邮编（约束：senderZipCode must be less than or equals to 16） */
  senderZipCode?: string
  /**创建时间 */
  createTime?: number
  /**更新时间 */
  updateTime?: number
  /**创建人 */
  createBy?: string
  /**更新人 */
  updateBy?: string
  /**1未删除,0已删除 */
  valid?: boolean
  /**邮编配置类型 */
  configZipcodeType?: number
  /**邮编配置seq */
  configZipcodeSeq?: string
  /**文件名 */
  uploadFileName?: string
  /**预签名文件地址 */
  presignUploadFileUrl?: string
  /**省市区配置 STANDARD( 1,"标准数据下发（默认）" ),ORIGIN( 2,"原始数据下发" ) */
  addressLangType?: number
  /** 轨迹追踪网址,所属表字段为channel_express_type_config.trace_url */
  traceUrl?: string
}

interface ExpressTypeAdapterDTO {
  /**编号 */
  expressTypeSeq?: string
  /**服务产品编码 */
  expressType?: string
  /**服务产品编码 */
  expressTypeCode?: string
  /**服务产品名称 */
  expressTypeName?: string
  /**物流公司代号 */
  companyCode?: string
  /**物流公司代号 */
  providerCode?: string
  /**顺序 */
  orderNum?: number
  /**租户ID */
  appId?: string
  /**承运商公司编码 */
  companySeq?: string
  /**承运商公司编码 */
  providerSeq?: string
  /**承运商公司名称 */
  companyName?: string
  /**承运商公司名称 */
  providerName?: string
  /**承运商产品编码 */
  channelExpressType?: string
  /**承运商产品名称 */
  channelExpressTypeName?: string
  /**是否校验地址，0-否，1-是 */
  validateAddress?: boolean
  /**揽收区域名称 */
  pickUpAreaName?: string
  /**配送区域名称 */
  deliveryAreaName?: string
  /**增值服务：
1=代收货款
2=保价服务
3=包装服务
4=送货上楼
5=保鲜服务
6=返单服务 */
  valueAddedServices?: number[]
  /**支持的结算方式：
1=寄付现结
2=到付现结
3=寄付月结
4=到付月结 */
  settlementModes?: number[]
  /**服务产品支持的支付方式
【zeek到付新增】
1-寄付现结
2-到付现结
3-寄付月结 */
  expressTypeSettlementModes?: number[]
  /**承诺送达时间/物流时效 */
  promisedTime?: string
  /**取件区域 */
  pickUpAreaCodes?: AreaCodesWrapperDTO[]
  /**配送区域 */
  deliveryAreaCodes?: AreaCodesWrapperDTO[]
  /**包裹限制类型：0=无限制; 1=尺寸上限; 2=周长上限; 3=边长总和上限 */
  packageSizeLimitType?: number
  /**长度/最大边长限制 */
  lengthLimit?: number
  /**宽度限制 */
  widthLimit?: number
  /**高度限制 */
  heightLimit?: number
  /**长度计量单位（包括长宽高周长等）：cm; in */
  lengthUnit?: string
  /**重量单位：kg; lb */
  weightUnit?: string
  /**重量限制 */
  weightLimit?: number
  /**长度总和，取决于package_size_limit_type(2=表示最大边长和周长总和, 3=表示三边边长总和) */
  sumLengthLimit?: number
  /**备注 */
  remark?: string
  /**支持的币种 */
  supportedCurrencies?: string[]
  /**支持的配送地址类型: */
  supportedAddressType?: string[]
  /**支持温层属性：1-常温 2-冷藏 3-冷冻 */
  storageCdt?: number[]
  /**结算属性 1-代收货款  2-线上支付
ec2.0新增 */
  supportedCheckoutTypes?: number[]
  /**服务产品支持的结算属性 1-代收货款  2-线上支付 */
  expressTypeSupportedCheckoutTypes?: number[]
  /**服务产品支持的揽收方式：1:PICK_UP;2:DROP_OFF */
  supportedPickupTypes?: number[]
  /**服务产品支持的揽收方式：1:PICK_UP;2:DROP_OFF */
  expressTypeSupportedPickupTypes?: number[]
  /**关联渠道列表值,可多选。
0普货、
1.内置电池、
2.利器、
3.液体、
4.粉末、
5.膏体、
6.纯电池
7.武器、
8.情趣品、
9.纺织品、
10.危险品。 */
  supportedCargoTypes?: number[]
  /**COD币种类型: 1 关联目的国币种 2 指定币种,指定币种则取supported_currencies */
  codCurrencyType?: number
  /**最大支持的COD金额，乘以100存储 */
  maxCodAmount?: number
  /**服务产品类型
   * SAAS_SERVICE(),
   * DOMESTIC_LOGISTICS_ORDER( 3,"本土物流订单" ),
   * CROSS_BORDER_PARCEL_ENTIRE( 4,"跨境小包一单到底" ),
   * WAREHOUSING_ORDER_2C( 5,"仓储订单2C" ),
   * WAREHOUSING_DISTRIBUTION_ORDER_2C( 6,"仓配订单2C" ),
   * WAREHOUSING_DISTRIBUTION_ORDER_2C_NON_OMS( 7,"仓配订单2C(非OMS串接)" ),
   * ON_DEMAND_INFO( 8,"即时物流" )
   */
  channelRelationType?: number
  /**启停状态: 1启用 0停用 */
  status?: number
  /**是否支持保险服务 0否1是 */
  insuranceSupport?: number
  /**服务产品对应的保额区间 */
  insuranceSetting?: ExpressTypeInsuranceSettingRespDTO
  /**可揽收时间 */
  selectablePickTimes?: SelectablePickTimeDTO[]
  /**到付现结 支付方式支持结果
【zeek到付新增】
1-支持，未授权
2-支持，已授权
3-不支持 */
  codPayTypeSupport?: number
  /**授权排序（平台授权默认为1） */
  configOrder?: number
  /**支持的税号类型列表 */
  supportedTaxNoTypes?: string[]
  /** 是否支持EORI：0-否，1-是,所属表字段为express_type.supported_eori */
  supportedEori?: number
  /**渠道产品配置 （默认发件信息） */
  channelExpressTypeConfig?: ChannelExpressTypeConfigDTO
  /**是否渠道预估费用 */
  channelCalculateFee?: number
  /** 是否支持预约派送：0-否，1-是,所属表字段为express_type.supported_appoint_delivery */
  supportedAppointDelivery?: number
  /**支持的其他服务 */
  supportedOtherServices?: number[]
  /**运费金额 */
  freight?: number
  /**运费币种 */
  currencyCode?: string
  /**运费错误信息 */
  freightMsg?: string
  /**是否是即时物流 */
  isOnDemandInfo?: boolean
  /**是否是saas */
  isSaas?: boolean
  /** 是否支持运费到付权益扣除：0-否，1-是 */
  supportedCodRightStatus?: number
  /** 支持运费到付权益扣除的操作平台列表 */
  supportedCodRightOperatePlatform?: string[]
  /**签收服务类型 */
  signServiceType?: string
}
export interface WarehouseExpress {
  /**物流渠道id */
  warehouseExpressId?: string
  /**商家物流渠道id */
  sellerExpressId?: string
  /**
   * ow物流名称 - 废弃
   * @deprecated
   *  */
  logisticsName?: string
  /**渠道商名称 - 废弃 */
  channelName?: string
  /**服务商物流名称 - 废弃 */
  expressName?: string
  /**服务商物流编码 */
  expressCode?: string
  /** 对接平台 - 废弃 */
  source?: string
  /** 平台物流编码 - 废弃 */
  platformCode?: string
  /** 运送方式，home-宅配，store-店配, 多选逗号分隔 */
  deliveryType?: 'home' | 'store'
  /** 物流类型provider: 服务商物流产品,platform 平台串接 */
  logisticsType?: string
  /** 仓库服务商物流商编码 */
  providerLogisticsCode?: string
  /** logisticsChannel 匹配expressCode得出 */
  logisticsChannel?: string
  /**oms服务产品编码 */
  omsExpressCode?: string
  /**平台物流匹配方式 1-全匹配 2-包含  - 废弃 */
  matchType?: number
  /**商家配置渠道启用状态  - 废弃 */
  sellerEnable?: boolean
  /**仓储配置渠道启用状态  - 废弃 */
  enable?: boolean
  /**  - 废弃 */
  sortNum?: number
  /**运营运费支付设置 */
  freightPaymentSetting?: string[]
  /**商家运费支付设置 */
  freightPaymentSeller?: string[]
  /**订单COD币种 */
  codCurrency?: string
  /**订单申报币种 */
  orderCurrency?: string
  /**OW物流编码 */
  logisticsId?: string
  /**渠道商编码 */
  logisticsProviderId?: string
  /** 物流名称-多语言 */
  logisticsNames?: any
  /** 渠道商名称-多语言 */
  logisticsProviderNames?: any
  /**商家服务产品信息 */
  expressType?: ExpressTypeAdapterDTO
  sellerExpressConfig?: {
    expressTypeName: string
    expressType: string
    companyName: string
    updateTime: string
    callbackKey: string
    extInfo: string
    configSeq: number
    createBy: string
    sellerId: string
    createTime: string
    updateBy: string
    traceCallbackUrl: string
    appKey: string
    appSecret: string
    id: number
    channelCode: string
  }
  /**
   * 店配-门店数据来源，lms-LMS门店地址库，other-包含电子地图等
   */
  stationAddressSource: string
  stationCompanyCode: string
  stationTypes: IStationTypes[]
  buyInsurance?: boolean
  buyFreeInsurance?: boolean
  defaultInsuranceConfig?: 'no' | 'yes' | 'force'
  supportBuyInsurance?: boolean
  /** 商家自定义匹配规则开关 */
  sellerCustomMatch: boolean
  /** 物流匹配规则： system 系统规则 custom 自定义规则 */
  logisticsMatchRule: string
  /** 仓库物流匹配设置 */
  matchConfig: LogisticMatchConfig
  /** 商家物流匹配规则 */
  sellerMatchConfig: LogisticSellerMatchConfig
  /** 是否要发票 */
  invoiceSetting: string
  /**支持退货 */
  supportRefund?: boolean
}
export type IStationTypes = {
  stationType: number
  stationTypeName: string
}

export interface Warehouse {
  country?: string
  address?: string
  warehouseId?: string
  providerId?: string
  enable?: boolean
  warehouseName?: string
  providerName?: string
  warehouseTpwId?: string
  warehouseExpresses?: WarehouseExpress[]
  unConfigSaasWarehouseExpresses?: WarehouseExpress[]
}

export interface SellerWarehouse {
  country?: string
  province?: string
  city?: string
  district?: string
  town?: string
  address?: string
  warehouseId?: string
  providerId?: string
  enable?: boolean
  warehouseName?: string
  warehouseNames?: Record<string, string>
  providerName?: string
  storageTypeList?: (number | string)[]
  warehouseExpresses?: WarehouseExpress[]
  /** 是否支持不良品 */
  supportBadGoodsOutbound?: boolean
  multiLogisticsSupported?: boolean
}

interface Res {
  code: string
  errors: string[]
  message: string
}

export interface WarehouseRes extends Res {
  data?: SellerWarehouse[]
}

export interface UpdateBarcodeParams {
  language?: string
  skus: {
    skuId: string
    barcode: string
  }[]
}

/** 作废入库单 */
export const cancelOrderApi = async (data) => await http.admin.post(warehouseUrl.cancelOrder, data)
/** 标记已完成 */
export const completeOrderApi = async (data) => await http.admin.post(warehouseUrl.complete, data)
/** 入库单列表 */
export const getWarehoueListApi = (data: Types.InboundsSearchReq) => http.admin.post<Types.InboundsSearchRes>('/api/inbounds/search', data)
/** 入库单商品分页列表 */
export const goodsListDetailApi = async (params: Types.InboundsDetailReq) => {
  const res = await http.admin.get<Types.InboundsDetailRes>('/api/inbounds/detail', { params })
  res.data?.list?.forEach((item) => {
    item.batches?.forEach((b) => {
      if (b.expireDate && b.expireDateTimezone) {
        b.expireDate = formatTimezone(b.expireDate, b.expireDateTimezone)
      }
    })
  })
  return res
}
/** 入库单基础信息详情 */
export const baseInfoApi = (params: Types.InboundsGetReq) => http.admin.get<Types.InboundsGetRes>('/api/inbounds/get', { params })
/** 创建入库单 */
export const createApi = async (data) => await http.admin.post(warehouseUrl.create, data)
/** 编辑个人入库单 */
export const editApi = async (data) => await http.admin.post(warehouseUrl.edit, data)
/** 打印入库单详情 */
export const getInboundPrintApi = async (params) => await http.admin.get(warehouseUrl.getOrder, { params })
/** 获取出货配置 */
export const getOutboundsSetting = async () =>
  await http.admin.get<Types.SettingGetRes>(warehouseUrl.getOutboundsSetting, {
    params: {
      t: Date.now(),
    },
  })

export interface SellerSettingAutoChoseWarehouseReqDTO {
  /**语言 */
  language?: string
  /**启用的仓库列表 */
  autoWarehouseList?: string[]
}

/** 修改商家自动选仓配置 */
export const updateAutoChooseWarehouse = (params?: SellerSettingAutoChoseWarehouseReqDTO) => http.admin.post('/api/settings/update-auto-chose-warehouse', params)
/** 更新出货配置 */
export const updateOutboundsSetting = async (data) => await http.admin.post(warehouseUrl.updateOutboundsSetting, data)
/** 更新自动翻译商品名设置 */
export const updateTranslationSetting = async (data) => await http.admin.post(warehouseUrl.updateTranslationSetting, data)

/** 获取是否绑定了可以上传的销售平台 */
export const settingCheck = async (data) => await http.admin.get(warehouseUrl.settingCheck, data)
/** 更新国际条码 */
export const updateBarcode = async (data: UpdateBarcodeParams) => await http.admin.post('/api/skus/update-barcode', data)

/** 下载入库单接口 */
export const getLabelUrl = retryWhen(
  (inboundOrderId: string, headers = {}) =>
    http.admin.get<{
      finish?: boolean
      url?: string
      language?: string
    }>(warehouseUrl.getLabelUrl, {
      params: {
        inboundOrderId,
      },
      headers,
    }),
  {
    maxRetry: 9,
    when(response) {
      return !response.data.finish
    },
  }
)

export const getLabelUrlOnce = ({ inboundOrderId, inboundWaybillId }, headers: Record<string, string>) => {
  return http.admin.get<{
    finish?: boolean
    url?: string
    language?: string
  }>(warehouseUrl.getLabelUrl, {
    params: {
      inboundOrderId,
      inboundWaybillId,
    },
    headers,
  })
}
export const calcInsurance = (data: Types.CalculateInsuranceReq) =>
  http.admin.post<Types.CalculateInsuranceRes>(warehouseUrl.calcInsurance, data).then((res) => {
    yuanify(res.data, [
      'minInsuredAmount',
      'maxInsuredAmount',
      'insuranceAmount',
      'skuTotalAmount',
      'freeMinInsuredAmount',
      'freeMaxInsuredAmount',
      'freeInsuranceAmount',
      'freeSkuTotalAmount',
      'convertedSkuTotalAmount',
    ])
    return res
  })
/**批量作废入库单 */
export const inboundOrderBathchVoidApi = async (data) => await http.admin.post(warehouseUrl.bathchCancelUrl, data)

export const getTransportProviders = createFnWithUrl((uri) => http.admin.post<Types.TransportProviders[]>(uri), '/api/logistics/list-transport-provider')

export const getInboundTrackList = createFnWithUrl(
  (uri, data: Types.InboundTraceListReqDTO) => http.admin.post<Types.InboundTraceListResDTO>(uri, data),
  '/api/inbounds/track/list'
)

export const inboundFetchSupportBatches = (data: Types.InboundMultiBatchesCheckReqDTO) => http.admin.post<Types.SupportRes>('/api/inbounds/multi_batches/support', data)

interface GetLabelParamReq {
  /** 入库单id（约束：元素个数范围：[0,64]） */
  inboundOrderId: string
}

interface GetLabelParamRes {
  /**是否需要走异步下载 */
  async?: boolean
}

/**
 * **接口** [获取下载入库单参数↗](https://apidoc.inshopline.com/#/project/Java/80/feature%2F4025_inbound_sku_limit/interface/9e71b217a797d2f7a60771a092b0ed71)
 * @请求头 `GET /api/inbounds/get-label-param`
 * @更新时间 `2024-07-01 18:18:59`
 */
export function getGetLabelParam(params: GetLabelParamReq) {
  return http.admin.get<GetLabelParamRes>(`/api/inbounds/get-label-param`, {
    params,
    toast: true,
  })
}

interface DownloadLabelAsyncReq {
  /** 入库单id（约束：元素个数范围：[0,64]） */
  inboundOrderId: string
  /**是否下载唛头，默认false */
  downloadMark?: boolean
  /**是否下载入库单，默认false */
  downloadOrder?: boolean
}

interface DownloadLabelAsyncRes {
  batchTaskId?: string
}

/**
 * **接口** [异步生成入库单↗](https://apidoc.inshopline.com/#/project/Java/80/feature%2F4025_inbound_sku_limit/interface/16759937fc9069b34d69745fd73387ee)
 * @请求头 `POST /api/inbounds/download-label-async`
 * @更新时间 `2024-07-01 18:18:59`
 */
export function postDownloadLabelAsync(data: DownloadLabelAsyncReq) {
  return http.admin.post<DownloadLabelAsyncRes>(`/api/inbounds/download-label-async`, data, { toast: true })
}
